export interface SignalingMessage {
    type: 'welcome' | 'peer-joined' | 'peer-left' | 'existing-peers' | 'offer' | 'answer' | 'ice-candidate' | 'ping' | 'pong' | 'request-peers' | 'request-peer-list' | 'announce' | 'join-event' | 'event-peers' | 'device-identification' | 'reconnection-available' | 'check-event-status' | 'event-status-response' | 'event-data' | 'count-update' | 'system' | 'attendance-update' | 'attendance-update-ack' | 'device-info-update';
    clientId?: string;
    fromId?: string;
    targetId?: string;
    totalClients?: number;
    peers?: string[] | Array<{
        clientId: string;
        deviceId?: string;
        deviceName?: string;
        platform?: string;
        userDisplayName?: string;
        deviceCount?: number;
        attendeeCount?: number;
    }>;
    data?: any;
    // Event-based connection support
    eventId?: string;
    eventToken?: string;
    serverUrl?: string;
    // Device identification
    deviceId?: string;
    deviceName?: string;
    platform?: string;
    userDisplayName?: string;
    // Reconnection data
    lastSeen?: number;
    activePeers?: number;
    // Event status data
    status?: {
        eventId: string;
        exists: boolean;
        hasActivePeers: boolean;
        activePeerCount: number;
        wasPreviouslyConnected: boolean;
        isWithin8Hours: boolean;
        shouldReconnect: boolean;
        lastSeenAt?: number;
        eventCreatedAt?: string;
    };
    shouldReconnect?: boolean;
    attendeeCount?: number;
    deviceCount?: number;
    totalCount?: number;
    // Properties for count-update messages
    count?: number;
    senderId?: string;
    senderName?: string;
    timestamp?: number; // Added timestamp
    password?: string; // Added password field for event joining
    // NEW: Add toId for direct messaging
    toId?: string;
}

export interface WebRTCPeer {
    id: string;
    connection: RTCPeerConnection;
    dataChannel?: RTCDataChannel;
    localStream?: MediaStream;
    remoteStream?: MediaStream;
    isConnected: boolean;
    isInitiator: boolean;
    // Enhanced device information for better peer identification
    deviceId?: string;
    deviceName?: string;
    platform?: string;
    lastSeen?: number;
    // User identification (if available via contacts)
    userPhoneNumber?: string;
    userName?: string;
    userDisplayName?: string;
    abbreviatedName?: string;
    // Event-based connection support
    eventId?: string;
    isEventVerified?: boolean;
    // Event data synchronization
    attendeeCount?: number;
    eventCount?: number;
    deviceCount?: number; // NEW: Device-specific count for audit purposes
    // Background tracking
    isInBackground?: boolean;
    lastBackgroundPing?: number;
    backgroundPingCount?: number;
    isCreator?: boolean;
}

export interface ChannelMessage {
    type: 'text' | 'file' | 'system' | 'device-info' | 'count_sync' | 'peer_count_update' | 'audit-request' | 'audit-response' | 'heartbeat-ping' | 'heartbeat-pong' | 'attendance_update' | 'request_attendance_audit' | 'ticket_sync_request' | 'shared_tickets' | 'background-ping' | 'request-count-consensus' | 'count-consensus-response' | 'count-sync-notification' | 'server-count-updated';
    data: any;
    timestamp: number;
    senderId: string;
    requesterId?: string;
    totalCount?: number;
    deviceCount?: number;
    // Enhanced device information for better peer identification
    senderDeviceId?: string;
    senderDeviceName?: string;
    senderPlatform?: string;
    // User identification (if available via contacts)
    senderPhoneNumber?: string;
    senderUserName?: string;
    senderDisplayName?: string;
    senderAbbreviatedName?: string;
    // Event synchronization data
    attendeeCount?: number;
    eventCount?: number;
    // Legacy compatibility properties
    tickets?: any[];
    ticket?: any;
    eventId?: string;
}

export interface WebRTCConfiguration {
    iceServers: RTCIceServer[];
    signalingServerUrl: string;
}

export interface PeerConnectionEvent {
    type: 'peer-connected' | 'peer-disconnected' | 'data-received' | 'stream-received' | 'auto-reconnected' | 'peers-cleaned';
    peerId: string;
    data?: any;
    stream?: MediaStream;
}

export enum ChannelType {
    DATA = 'data',
}

export interface ChannelConfig {
    ordered: boolean;
    maxRetransmits?: number;
    maxPacketLifeTime?: number;
}

// Configuration modes
export enum DeploymentMode {
    LOCAL = 'local',           // Local network (192.168.x.x)
    PUBLIC = 'public',         // Public IP access
    EXTERNAL = 'external'      // External WebSocket server
}

// Get current deployment mode from URL or environment
function getDeploymentMode(): DeploymentMode {
    const hostname = window.location.hostname;

    // Check if accessing via public IP
    if (hostname === '213.94.17.169') {
        return DeploymentMode.PUBLIC;
    }

    // Check if accessing via local IP
    if (hostname.startsWith('192.168.') || hostname.startsWith('10.') || hostname.startsWith('172.')) {
        return DeploymentMode.LOCAL;
    }

    // Default to local for localhost
    return DeploymentMode.LOCAL;
}

// Configuration factory
function createWebRTCConfig(): WebRTCConfiguration {
    const mode = getDeploymentMode();

    // Automatically determine WebSocket protocol based on page protocol
    const isSecure = window.location.protocol === 'https:';
    const wsProtocol = isSecure ? 'wss' : 'ws';
    const hostname = window.location.hostname;

    const baseConfig = {
        iceServers: [
            { urls: 'stun:stun.l.google.com:19302' },
            { urls: 'stun:stun1.l.google.com:19302' }
        ]
    };

    switch (mode) {
        case DeploymentMode.PUBLIC:
            return {
                ...baseConfig,
                signalingServerUrl: 'wss://213.94.17.169:8080'
            };

        case DeploymentMode.EXTERNAL:
            return {
                ...baseConfig,
                signalingServerUrl: 'wss://ws.aforo.bink.tools:3000'  // Updated to match production server port
            };

        case DeploymentMode.LOCAL:
        default:
            return {
                ...baseConfig,
                signalingServerUrl: `${wsProtocol}://${hostname}:8080`
            };
    }
}

// export const DEFAULT_WEBRTC_CONFIG: WebRTCConfiguration = createWebRTCConfig();

// Manual override options (uncomment to force a specific mode):
export const DEFAULT_WEBRTC_CONFIG: WebRTCConfiguration = {
    iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' }
    ],
    signalingServerUrl: 'wss://ws.aforo.bink.tools:8081'  // WebRTC mesh port
    // signalingServerUrl: 'wss://ws.aforo.bink.tools:3000'  // Alternative port
    // signalingServerUrl: 'wss://213.94.17.169:8080'  // Force public IP
    // signalingServerUrl: `ws://${window.location.hostname}:8080`  // Force local HTTP
}; 